import { Link } from 'react-router-dom';
import { 
  Twitter, 
  Github, 
  MessageCircle, 
  Send,
  Shield,
  Lock,
  Clock,
  Headphones,
  Zap,
  Award,
  Globe
} from 'lucide-react';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    products: [
      { label: 'Exchange', path: '/' },
      { label: 'Buy Crypto', path: '/buy' },
      { label: 'Sell Crypto', path: '/sell' },
      { label: 'Mobile App', path: '/mobile' },
      { label: 'API', path: '/api' },
      { label: 'Widget', path: '/widget' },
      { label: 'White Label', path: '/white-label' },
    ],
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Careers', path: '/careers' },
      { label: 'Blog', path: '/blog' },
      { label: 'Press Kit', path: '/press' },
      { label: 'Partners', path: '/partners' },
      { label: 'Affiliate Program', path: '/affiliate' },
      { label: 'Referral Program', path: '/referral' },
    ],
    support: [
      { label: 'Help Center', path: '/help' },
      { label: 'Contact Us', path: '/contact' },
      { label: 'Status Page', path: '/status' },
      { label: 'Supported Coins', path: '/supported-coins' },
      { label: 'List a Coin', path: '/list-a-coin' },
      { label: 'Bug Bounty', path: '/bug-bounty' },
      { label: 'FAQ', path: '/faq' },
    ],
    legal: [
      { label: 'Terms of Service', path: '/terms' },
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Cookie Policy', path: '/cookies' },
      { label: 'AML/KYC Policy', path: '/aml-kyc' },
    ],
  };

  const socialLinks = [
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: MessageCircle, href: 'https://discord.com', label: 'Discord' },
    { icon: Send, href: 'https://t.me', label: 'Telegram' },
    { icon: Github, href: 'https://github.com', label: 'GitHub' },
  ];

  const features = [
    { icon: Shield, title: 'Non-custodial', desc: 'You control your keys' },
    { icon: Lock, title: 'Private', desc: 'No KYC required' },
    { icon: Clock, title: 'Fast', desc: 'Avg. 2 minutes' },
    { icon: Headphones, title: '24/7 Support', desc: 'Always here to help' },
  ];

  return (
    <footer className="footer">
      {/* Features Bar */}
      <div className="footer-features">
        <div className="features-container">
          {features.map((feature, index) => (
            <div key={index} className="feature-item">
              <div className="feature-icon-box">
                <feature.icon className="feature-icon" />
              </div>
              <div className="feature-content">
                <h4 className="feature-title">{feature.title}</h4>
                <p className="feature-desc">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Footer */}
      <div className="footer-main">
        <div className="footer-container">
          {/* Brand Column */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <div className="logo-mark">
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="12" fill="url(#footer-logo-gradient)"/>
                  <path d="M20 10L25 20L20 24L15 20L20 10Z" fill="white"/>
                  <path d="M20 24L25 20L20 30L15 20L20 24Z" fill="white" fillOpacity="0.7"/>
                  <defs>
                    <linearGradient id="footer-logo-gradient" x1="0" y1="0" x2="40" y2="40">
                      <stop stopColor="#2563eb"/>
                      <stop offset="1" stopColor="#06b6d4"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <span className="logo-text">Omini<span className="gradient-text">Bridge</span></span>
            </Link>
            <p className="footer-description">
              Fast, private, and non-custodial cryptocurrency exchange. 
              Swap 1000+ crypto assets instantly with the best rates.
            </p>
            
            <div className="footer-badges">
              <div className="badge">
                <Award className="badge-icon" />
                <span>Best Rate Guarantee</span>
              </div>
              <div className="badge">
                <Globe className="badge-icon" />
                <span>Global Coverage</span>
              </div>
            </div>

            <div className="footer-social">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label={social.label}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div className="footer-links">
            <div className="links-column">
              <h4 className="links-title">Products</h4>
              <ul className="links-list">
                {footerLinks.products.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="links-column">
              <h4 className="links-title">Company</h4>
              <ul className="links-list">
                {footerLinks.company.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="links-column">
              <h4 className="links-title">Support</h4>
              <ul className="links-list">
                {footerLinks.support.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="links-column">
              <h4 className="links-title">Legal</h4>
              <ul className="links-list">
                {footerLinks.legal.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <div className="bottom-container">
          <p className="copyright">
            © {currentYear} Omini Bridge. All rights reserved.
          </p>
          <div className="bottom-links">
            <span className="powered-by">
              <Zap className="zap-icon" />
              Powered by Advanced Exchange Engine
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
